import { AuthUser } from './global';

interface CacheKeyData {
  audience: string;
  scope: string;
  client_id: string;
}

export class CacheKey {
  public client_id: string;
  public scope: string;
  public audience: string;

  constructor(data: CacheKeyData, public prefix: string = keyPrefix) {
    this.client_id = data.client_id;
    this.scope = data.scope;
    this.audience = data.audience;
  }

  toKey(): string {
    return `${this.prefix}::${this.client_id}::${this.audience}::${this.scope}`;
  }

  static fromKey(key: string): CacheKey {
    const [prefix, client_id, audience, scope] = key.split('::');

    return new CacheKey({ client_id, scope, audience }, prefix);
  }
}

interface CacheEntry {
  audience: string;
  scope: string;
  client_id: string;
  // id_token: string;
  access_token: string;
  expires_in: number;
  refresh_token?: string;
  user_info?: AuthUser;
}

export interface ICache {
  save(entry: CacheEntry): void;
  get(
    key: CacheKey,
    expiryAdjustmentSeconds?: number
  ): Partial<CacheEntry> | undefined;
  clear(): void;
}

const keyPrefix = '@@da-auth-js@@';
const DEFAULT_EXPIRY_ADJUSTMENT_SECONDS = 0;

type CachePayload = {
  body: Partial<CacheEntry>;
  expiresAt: number;
};

/**
 * Wraps the specified cache entry and returns the payload
 * @param entry The cache entry to wrap
 */
const wrapCacheEntry = (entry: CacheEntry): CachePayload => {
  // const expiresInTime = Math.floor(Date.now() / 1000) + entry.expires_in;
  // const expirySeconds = Math.min(expiresInTime, entry.decodedToken.claims.exp);
  const expirySeconds =Math.floor(Date.now() / 1000) + entry.expires_in;

  return {
    body: entry,
    expiresAt: expirySeconds
  };
};

export class LocalStorageCache implements ICache {
  public save(entry: CacheEntry): void {
    const cacheKey = new CacheKey({
      client_id: entry.client_id,
      scope: entry.scope,
      audience: entry.audience
    });
    const payload = wrapCacheEntry(entry);

    window.localStorage.setItem(cacheKey.toKey(), JSON.stringify(payload));
  }

  public get(
    cacheKey: CacheKey,
    expiryAdjustmentSeconds = DEFAULT_EXPIRY_ADJUSTMENT_SECONDS
  ): Partial<CacheEntry> | undefined {
    const nowSeconds = Math.floor(Date.now() / 1000);
    // const payload = this.readJson(cacheKey);

    const cacheItem = cacheKey && window.localStorage.getItem(cacheKey.toKey());

    if (!cacheItem) return;

    const payload = JSON.parse(cacheItem);

    if (!payload) return;

    if (payload.expiresAt - expiryAdjustmentSeconds < nowSeconds) {
      if (payload.body.refresh_token) {
        const newPayload =  {
          body: { refresh_token: payload.body.refresh_token },
          expiresAt: payload.expiresAt
        };
        // const newPayload = this.stripData(payload);
        // this.writeJson(cacheKey.toKey(), newPayload);

        return newPayload.body;
      }

      localStorage.removeItem(cacheKey.toKey());
      return;
    }

    return payload.body;
  }

  public clear() {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      if(localStorage.key(i) && localStorage.key(i)!.startsWith(keyPrefix)) {
          localStorage.removeItem(localStorage.key(i)!);
      }
    }
  }
}

export class InMemoryCache {
  public enclosedCache: ICache = (function () {
    let cache: Record<string, CachePayload> = {};

    return {
      save(entry: CacheEntry) {
        const cacheKey = new CacheKey({
          client_id: entry.client_id,
          scope: entry.scope,
          audience: entry.audience
        });
        // const payload = wrapCacheEntry(entry);
        // cache[cacheKey.toKey()] = payload;

        cache[cacheKey.toKey()] = wrapCacheEntry(entry);
      },

      get(
        cacheKey: CacheKey,
        expiryAdjustmentSeconds = DEFAULT_EXPIRY_ADJUSTMENT_SECONDS
      ): Partial<CacheEntry> | undefined {
        const nowSeconds = Math.floor(Date.now() / 1000);

        // const existingCacheKey = findExistingCacheKey(
        //   cacheKey,
        //   Object.keys(cache)
        // );

        // const wrappedEntry: CachePayload = cache[existingCacheKey];

        const existingCacheKey = cacheKey.toKey()
        const wrappedEntry: CachePayload = cacheKey && cache[existingCacheKey];

        if (!wrappedEntry) {
          return;
        }

        if (wrappedEntry.expiresAt - expiryAdjustmentSeconds < nowSeconds) {
          if (wrappedEntry.body.refresh_token) {
            wrappedEntry.body = {
              refresh_token: wrappedEntry.body.refresh_token
            };

            return wrappedEntry.body;
          }

          delete cache[cacheKey.toKey()];

          return;
        }

        return wrappedEntry.body;
      },

      clear() {
        cache = {};
      }
    };
  })();
}
